/**
 * PanelFooter — the booking panel's bottom action bar (the Practice app's, 15 Oct).
 * One full-width primary button; a centred hint above it names the NEXT missing step while the form is incomplete.
 * Create draws the design's secondary Close under it (SchedBookingFooter), the same handler as the header's ×. Edit puts "Cancel" (secondary, left) beside "Save changes" (primary, takes the
 * rest of the row) on one line; Cancel closes without saving, the same handler as the ×.
 */

import { Button } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';

interface PanelFooterProps {
  mode: 'create' | 'edit' | 'reschedule' | 'cancel';
  isSubmitting: boolean;
  isValid: boolean;
  /** Fields blocking save: the next one is named in the hint line. */
  missingFields?: string[];
  /** Raw RHF errors — diagnostic-only, dumped to a data attribute. */
  rawErrors?: unknown;
  onSave: () => void;
  /** Edit mode: the Cancel button beside Save (same handler as the header ×). */
  onCancel?: () => void;
  onReschedule?: () => void;
  onCancelAppointment?: () => void;
  /** When true the panel is inside the full-view dialog: roomier padding, heavier top border. */
  modal?: boolean;
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, (key, v) => {
      // Strip only the `ref` field itself (it's a DOM node — not serialisable).
      if (key === 'ref') return '[ref]';
      return v;
    });
  } catch {
    return '{}';
  }
}

/** Raw RHF field paths to the step they stand for, in Practice's words. */
const FIELD_LABELS: Record<string, string> = {
  'provider.id': 'a provider',
  provider: 'a provider',
  providerId: 'a provider',
  'location.id': 'a location',
  location: 'a location',
  'appointmentProfile.id': 'an appointment type',
  appointmentProfile: 'an appointment type',
  profileId: 'an appointment type',
  appointmentProfileId: 'an appointment type',
  slotId: 'a date and time',
  slotStart: 'a date and time',
  slot: 'a date and time',
  date: 'a date',
  selectedDate: 'a date',
  'patient.id': 'a patient',
  patient: 'a patient',
  patientId: 'a patient',
  reasonCode: 'a reason for visit',
  'reasonCode.code': 'a reason for visit',
  referenceAppointmentId: 'a previous appointment',
  residentialAddressId: 'an address',
  reminderProfileId: 'a reminder',
  sponsorId: 'an agreement',
};

/** Booking flow order (main's: location, then provider): the hint names the NEXT step only. */
const FLOW_ORDER: { key: string; target: string }[] = [
  { key: 'location', target: 'location' },
  { key: 'providerId', target: 'provider' },
  { key: 'appointmentProfileId', target: 'visit-type' },
  { key: 'reasonCode', target: 'reason' },
  { key: 'slotId', target: 'slot' },
  { key: 'patientId', target: 'patient' },
  { key: 'referenceAppointmentId', target: 'previous-appointment' },
  { key: 'residentialAddressId', target: 'address' },
  { key: 'reminderProfileId', target: 'reminder' },
  { key: 'sponsorId', target: 'payment' },
];

function nextHint(fields: string[]): { label: string; target: string } | null {
  for (const { key, target } of FLOW_ORDER) {
    const label = FIELD_LABELS[key];
    if (label && fields.some((f) => FIELD_LABELS[f] === label)) {
      return { label, target };
    }
  }
  const first = fields[0];
  if (!first) return null;
  return {
    label: FIELD_LABELS[first] ?? first.replace(/([A-Z])/g, ' $1').replace(/\./g, ' ').trim().toLowerCase(),
    target: '',
  };
}

function scrollTo(target: string) {
  const el = document.querySelector(`[data-completion-target="${target}"]`);
  el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // A target may mark its control with data-completion-focus: focus it without a second scroll (opens nothing).
  el?.querySelector<HTMLElement>('[data-completion-focus]')?.focus({ preventScroll: true });
}

export function PanelFooter({
  mode,
  isSubmitting,
  isValid,
  missingFields,
  rawErrors,
  onSave,
  onCancel,
  onReschedule,
  onCancelAppointment,
  modal = false,
}: PanelFooterProps) {
  const wrapCls = cn('booking-foot', modal && 'booking-foot-modal');

  if (mode === 'cancel') {
    return (
      <div className={wrapCls}>
        <Button variant="destructive" size="lg" fullWidth onClick={onCancelAppointment} disabled={isSubmitting}>
          {isSubmitting ? 'Cancelling...' : 'Cancel appointment'}
        </Button>
        {onCancel ? (
          <Button variant="secondary" size="lg" fullWidth onClick={onCancel}>
            Close
          </Button>
        ) : null}
      </div>
    );
  }

  const next = !isValid && missingFields && missingFields.length > 0 ? nextHint(missingFields) : null;

  const saveButton = (
    <Button
      size="lg"
      fullWidth
      onClick={onSave}
      disabled={!isValid || isSubmitting}
      data-form-errors={safeStringify(rawErrors)}
    >
      {isSubmitting ? 'Saving...' : mode === 'create' ? 'Book appointment' : 'Save changes'}
    </Button>
  );

  return (
    <div className={wrapCls}>
      {mode === 'edit' && onReschedule && (
        <Button variant="secondary" size="lg" fullWidth onClick={onReschedule}>
          Reschedule
        </Button>
      )}
      {next && (
        <p className="booking-foot-hint">
          Select{' '}
          <button type="button" onClick={() => scrollTo(next.target)} className="booking-foot-hint-link">
            {next.label}
          </button>{' '}
          to continue
        </p>
      )}
      {mode === 'edit' ? (
        <div className="booking-foot-row">
          <Button variant="secondary" size="lg" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          {saveButton}
        </div>
      ) : (
        <>
          {saveButton}
          {onCancel ? (
            <Button variant="secondary" size="lg" fullWidth onClick={onCancel}>
              Close
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}
